import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCoachSetup } from "../context/CoachSetupContext";
import { COACH_SETUP_PATH } from "../lib/coachSetup";
import { newUi } from "../lib/featureFlags";
import { LoadingSplash } from "./kit/LoadingSplash";

/**
 * Sends a coach who has not been through the short first-run set-up to
 * `/coach-setup` (issue #755), and nobody else anywhere — the coach
 * counterpart of `OnboardingGate.jsx`, mirrored deliberately for the same
 * three reasons that file's header gives: registration, signing in again
 * after skipping nothing (this screen has no partial state to abandon,
 * but a second sign-in must still not re-prompt), and reopening a tab
 * whose session cookie is still valid. One gate on the protected layout
 * covers all three, so `SignUpNew.jsx`/`Register.jsx` stay untouched —
 * they still navigate to `homePathForRole(role)` (`/coach-portal` for a
 * coach) and this gate intercepts, exactly as `OnboardingGate` intercepts
 * the player walkthrough.
 *
 * GATED ON `newUi("coach-setup")` ITSELF, NOT JUST THE ROUTE. The
 * `/coach-setup` route (`App.js`) already redirects to `/coach-portal`
 * when the flag is off, so a gate that redirected there unconditionally
 * would loop with the flag off: gate sends a required coach to
 * `/coach-setup` -> the route sees the flag off and bounces to
 * `/coach-portal` -> the gate (still `required`) sends them to
 * `/coach-setup` again. Checking the flag here, the same single source of
 * truth `CHROMELESS_ROUTES`' own `isChromelessRoute` uses, is what breaks
 * that loop: with the flag off this gate does nothing at all, and the
 * shipped behaviour (straight to `/coach-portal`) is untouched.
 *
 * WHAT IT NEVER DOES:
 *   - it never fires for a player or admin. `useCoachSetup` makes no
 *     request and reports nothing to do for a non-coach (see that
 *     provider's header), so this is not a role check that could rot.
 *   - it never redirects away FROM `/coach-setup` — the route renders for
 *     any coach who asks for it, flag on, which is what lets a coach who
 *     clicked away by mistake navigate straight back.
 *
 * WHY IT BLOCKS ON THE FIRST ANSWER, same as `OnboardingGate`: rendering
 * `/coach-portal` and then redirecting would show a brand-new coach their
 * empty roster for a beat — the exact screen this issue exists to insert
 * a step before — and would make the responsive harness measure whichever
 * page won the race.
 */
export default function CoachSetupGate({ children }) {
  const { user } = useAuth();
  const { required, loading } = useCoachSetup();
  const location = useLocation();

  if (user?.role !== "coach") return children;
  if (!newUi("coach-setup")) return children;

  if (loading) {
    return (
      <LoadingSplash testId="coach-setup-loading" />
    );
  }

  if (!required) return children;
  if (location.pathname.toLowerCase().replace(/\/+$/, "") === COACH_SETUP_PATH) return children;

  return <Navigate to={COACH_SETUP_PATH} state={{ from: location }} replace />;
}
